import React, { FC, useMemo, useState } from 'react';

// @ts-ignore
import { branch } from 'baobab-react/higher-order';

import {
  agentsSelector,
  clientsSelector,
  teamsSelector,
  observersSelector,
  gamesSelector,
} from '../../../../store/struct/selectors';

import USER_STRUCT from '../../../../store/struct/entities/user';
import TEAM_STRUCT from '../../../../store/struct/entities/team';

import Dialog from '../../../../components/dialog';
import Checkbox from '../../../../components/checkbox';
import { THEMES } from '../../../../components/button';
import Select from '../../../../components/form/select';
import Table, { TYPES as TABLE_TYPES } from '../../../../components/table';

import styles from './index.module.scss';

import { Participant } from '../../../../types';

const HEADERS = ['ФИО', 'Email', 'Выбрать'];

interface SelectUserFromPreviousGamesProps {
  games: any;
  clients: any;
  agents: any;
  observers: any;
  teams: any;
  participants: Participant[];
  handleCloseAddUsersDialog: () => void;
  handleAddUsers: (previousUsers: Participant[]) => void;
}

const SelectUserFromPreviousGames: FC<SelectUserFromPreviousGamesProps> = ({
  games,
  clients,
  agents,
  observers,
  teams,
  participants,
  handleCloseAddUsersDialog,
  handleAddUsers,
}) => {
  const [currentGameId, setCurrentGameId] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Participant[]>([]);

  const gamesOptions = useMemo(
    () => games.map((game: any) => ({ id: game.id, title: game.name })),
    [games],
  );
  const currentTeamsIds = teams
    .filter((team: any) => team[TEAM_STRUCT.GAME_ID] === currentGameId)
    .map((team: any) => team[TEAM_STRUCT.ID]);

  const currentClients = clients
    .filter((client: any) => client[USER_STRUCT.GAME_ID] === currentGameId)
    .map((client: any) => ({
      email: client[USER_STRUCT.EMAIL],
      name: client[USER_STRUCT.NAME],
      connections: [],
    }));
  const currentAgents = agents
    .filter(
      (agent: any) => currentTeamsIds.indexOf(agent[USER_STRUCT.TEAM_ID]) > -1,
    )
    .map((agent: any) => ({
      email: agent[USER_STRUCT.EMAIL],
      name: agent[USER_STRUCT.NAME],
      connections: [],
    }));
  const currentObservers = observers
    .filter((observer: any) => observer[USER_STRUCT.GAME_ID] === currentGameId)
    .map((observer: any) => ({
      email: observer[USER_STRUCT.EMAIL],
      name: observer[USER_STRUCT.NAME],
      connections: [],
    }));

  const currentUsers = currentClients
    .concat(currentAgents)
    .concat(currentObservers);

  const tableConfig = [
    {
      type: TABLE_TYPES.TEXT,
      getValue: (data: any) => data[USER_STRUCT.NAME],
    },
    {
      type: TABLE_TYPES.TEXT,
      getValue: (data: any) => data[USER_STRUCT.EMAIL],
    },
    {
      type: TABLE_TYPES.CHECKBOX,
      getValue: (data: any) => {
        const userIdx = selectedUsers.findIndex(
          user =>
            user.email === data[USER_STRUCT.EMAIL] &&
            user.name === data[USER_STRUCT.NAME],
        );
        if (userIdx > -1) {
          return true;
        } else {
          return false;
        }
      },
      onChange: (data: any) => {
        const userIdx = selectedUsers.findIndex(
          user =>
            user.email === data[USER_STRUCT.EMAIL] &&
            user.name === data[USER_STRUCT.NAME],
        );
        if (userIdx > -1) {
          let tempSelectedUsers = [...selectedUsers];

          tempSelectedUsers = [
            ...tempSelectedUsers.slice(0, userIdx),
            ...tempSelectedUsers.slice(userIdx + 1),
          ];
          setSelectedUsers(tempSelectedUsers);
        } else {
          let tempSelectedUsers = [...selectedUsers];
          tempSelectedUsers.push(data);
          setSelectedUsers(tempSelectedUsers);
        }
      },
      getProps: (data: any) => {
        const userIdx = participants.findIndex(
          participant => participant.email === data[USER_STRUCT.EMAIL],
        );
        return { className: styles.checkBoxContainer, disabled: userIdx > -1 };
      },
    },
  ];

  const handleChangeSelectAllUsers = (isChecked: boolean) => {
    if (isChecked) {
      const usersToRemove = new Set(
        participants.map((user: Participant) => user.email),
      );
      const filteredCurrentUsers = currentUsers.filter(
        (user: Participant) => !usersToRemove.has(user.email),
      );
      const usersToAdd = Array.from(
        new Map(
          [...selectedUsers, ...filteredCurrentUsers].map(item => [
            `${item.email}|${item.name}`,
            item,
          ]),
        ).values(),
      );
      setSelectedUsers(usersToAdd);
    } else {
      const usersToRemove = new Set(
        currentUsers.map((user: Participant) => `${user.email}|${user.name}`),
      );
      const filteredList = selectedUsers.filter(
        user => !usersToRemove.has(`${user.email}|${user.name}`),
      );
      setSelectedUsers(filteredList);
    }
  };

  const isAllUsersSelected = useMemo(() => {
    const set1 = new Set(
      selectedUsers.map(user => `${user.email}|${user.name}`),
    );
    const usersToRemove = new Set(
      participants.map((user: Participant) => user.email),
    );
    const filteredCurrentUsers = currentUsers.filter(
      (user: Participant) => !usersToRemove.has(user.email),
    );
    return filteredCurrentUsers.every((user: Participant) =>
      set1.has(`${user.email}|${user.name}`),
    );
  }, [currentUsers, participants, selectedUsers]);

  return (
    // @ts-ignore
    <Dialog
      title={`Выберите пользователей из предыдущих игр`}
      onCancel={handleCloseAddUsersDialog}
      cancelTitle={'Отмена'}
      buttons={[
        {
          id: 'add_previous_users',
          title: 'Добавить пользователей',
          theme: THEMES.PRIMARY,
        },
      ]}
      onClick={() => handleAddUsers(selectedUsers)}
      className={styles.addPreviousUsersDialogContainer}
      bodyClassName={styles.addPreviousUsersDialogContent}
    >
      <div>
        <div className={styles.block}>
          <Select
            onChange={(gameId: number) => {
              setCurrentGameId(gameId);
            }}
            options={gamesOptions}
            value={currentGameId}
            attrs={{ disabled: gamesOptions.length === 0 }}
            placeholder='Выбрать игру'
            onReset={undefined}
            className={undefined}
            selectablePlaceholder={undefined}
            disabled={undefined}
            resetAfterSelection={undefined}
            resetTrigger={undefined}
            showZeroValue={undefined}
          />
          {currentGameId !== null && (
            <div className={`${styles.centerizedRow}`}>
              <Checkbox
                id={`select_all_checkbox`}
                onChange={checked => {
                  handleChangeSelectAllUsers(checked);
                }}
                checked={isAllUsersSelected}
                className={styles.selectAllCheckbox}
              />
              <div className={styles.selectTitle}>{'Выбрать всех'}</div>
            </div>
          )}
        </div>
        {currentGameId !== null && (
          <div
            className={`${styles.centerizedRow} ${styles.infoTextContainer}`}
          >
            <div className={styles.infoText}>{'Выбрано пользователей:'}</div>
            <div className={styles.infoTextHighlited}>
              {selectedUsers.length}
            </div>
          </div>
        )}
        {currentGameId !== null ? (
          <Table
            className={styles.previousUsersTable}
            headers={HEADERS}
            config={tableConfig}
            data={currentUsers}
          />
        ) : (
          <div>
            Для того, чтобы открыть список пользователей, необходимо выбрать
            игру.
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default branch(
  {
    games: gamesSelector(),
    clients: clientsSelector(),
    agents: agentsSelector(),
    observers: observersSelector(),
    teams: teamsSelector(),
  },
  SelectUserFromPreviousGames,
);
